/**
 * Standard slippy-map tile coordinate math. Used to compute which registry sidecar file
 * (frontend/public/registry/<region>/<zoom>/<x>/<y>.json — see
 * backend/scripts/generate-registry-tiles.mts) covers a given map location.
 *
 * Deliberately duplicated rather than shared with the backend script: `@app/shared` is a
 * types-only package (its package.json exports map has no runtime condition), so it can't
 * carry a real function across the frontend/backend boundary. Both implementations are
 * unit-tested against the same known coordinates as a cheap cross-check that they agree.
 */

export interface TileCoord {
  x: number;
  y: number;
}

export function tileForLngLat(lng: number, lat: number, zoom: number): TileCoord {
  const n = 2 ** zoom;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y };
}

/**
 * The tapped tile plus its 8 neighbours, primary tile first. A tap lands inside the
 * polygon by definition, but not necessarily near its centroid — the sidecar shard a
 * building's units live in is keyed by the building's own centroid (see the generation
 * script), which can be one tile away from an edge tap at z16 (~600m tiles). Checking
 * neighbours too makes lookup robust to that without needing to reason about which
 * specific direction is safe to skip.
 */
export function tileWithNeighbors(lng: number, lat: number, zoom: number): TileCoord[] {
  const { x, y } = tileForLngLat(lng, lat, zoom);
  const coords: TileCoord[] = [{ x, y }];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue;
      coords.push({ x: x + dx, y: y + dy });
    }
  }
  return coords;
}
